<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import { ref, onMounted } from 'vue';
import CampaignsAPI from 'dashboard/api/campaigns';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  id: {
    type: Number,
    default: null,
  },
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['edit', 'delete', 'view', 'stats']);

const dispatchStats = ref(null);

const loadStats = async () => {
  if (props.isLiveChatType) return;
  try {
    const { data } = await CampaignsAPI.getStats(props.id);
    dispatchStats.value = data;
  } catch {
    // silencioso — stats são opcionais no card
  }
};

onMounted(loadStats);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value,
  'text-n-slate-12': !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  return props.status === STATUS_COMPLETED
    ? t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED')
    : t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>

      <!-- Mini resumo de disparos (apenas one-off) -->
      <div
        v-if="!isLiveChatType && dispatchStats && dispatchStats.total > 0"
        class="flex items-center gap-3 w-full mt-1"
      >
        <div
          class="flex-1 h-1.5 rounded-full bg-n-alpha-2 overflow-hidden flex"
        >
          <div
            class="h-full bg-n-teal-9 transition-all"
            :style="{
              width: dispatchStats.total
                ? Math.round((dispatchStats.sent / dispatchStats.total) * 100) +
                  '%'
                : '0%',
            }"
          />
          <div
            class="h-full bg-n-red-9 transition-all"
            :style="{
              width: dispatchStats.total
                ? Math.round(
                    (dispatchStats.failed / dispatchStats.total) * 100
                  ) + '%'
                : '0%',
            }"
          />
        </div>
        <span
          class="text-[11px] text-n-slate-11 flex-shrink-0 whitespace-nowrap"
        >
          <span class="text-n-teal-11 font-medium">{{
            dispatchStats.sent
          }}</span>
          enviados ·
          <span class="text-n-red-9 font-medium">{{
            dispatchStats.failed
          }}</span>
          falharam ·
          <span class="font-medium">{{ dispatchStats.pending }}</span> pendentes
          <span class="text-n-slate-10">/ {{ dispatchStats.total }}</span>
        </span>
      </div>
      <div
        v-else-if="
          !isLiveChatType && dispatchStats && dispatchStats.total === 0
        "
        class="mt-1"
      >
        <span class="text-[11px] text-n-slate-10 italic">
          Nenhum disparo registrado ainda
        </span>
      </div>
    </div>
    <div class="flex items-center justify-end gap-1 flex-shrink-0">
      <!-- Live chat: só configurar e excluir -->
      <template v-if="isLiveChatType">
        <Button
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-sliders-vertical"
          @click="emit('edit')"
        />
        <Button
          variant="faded"
          color="ruby"
          size="sm"
          icon="i-lucide-trash"
          @click="emit('delete')"
        />
      </template>
      <!-- One-off (WhatsApp/SMS): relatório + editar + excluir -->
      <template v-else>
        <Button
          v-tooltip="'Ver relatório de disparos'"
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-bar-chart-2"
          @click="emit('stats')"
        />
        <Button
          v-tooltip="'Editar campanha'"
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-pencil"
          @click="emit('view')"
        />
        <Button
          v-tooltip="'Excluir campanha'"
          variant="faded"
          color="ruby"
          size="sm"
          icon="i-lucide-trash"
          @click="emit('delete')"
        />
      </template>
    </div>
  </CardLayout>
</template>
